// Updated BookingForm.js - Matches the "Enter Details" image design
import React, { useState, useEffect } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  Container,
  Card,
  Form,
  Button
} from 'react-bootstrap';
import { FaArrowLeft, FaClock, FaCalendarAlt, FaGlobe, FaInfoCircle } from 'react-icons/fa';
import moment from 'moment';
const GUEST_STORAGE_KEY = 'meetify_guest';

const BookingFormComponent = ({ selectedDateTime, eventDetails, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    guestName: '',
    guestEmail: '',
    guestMobile: '',
    guestEmails: '',
    additionalInfo: '',
  });
  const [autoFilled, setAutoFilled] = useState(false);
  const [mobileError, setMobileError] = useState('');
  const [isBooking, setIsBooking] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  // Auto-fill from previously saved guest details
  useEffect(() => {
    try {
      const saved = localStorage.getItem(GUEST_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const hasData = parsed.name || parsed.email || parsed.mobile;
        if (hasData) {
          setFormData((prev) => ({
            ...prev,
            guestName: parsed.name || prev.guestName,
            guestEmail: parsed.email || prev.guestEmail,
            guestMobile: parsed.mobile || prev.guestMobile,
          }));
          setAutoFilled(true);
        }
      }
    } catch (err) {
      console.error('Error loading saved guest details:', err);
    }
  }, []);

  // Validate mobile number (digits only, 7-15 chars, optional leading +)
  const validateMobile = (value) => {
    if (!value) return ''; // empty is ok, handled by required attribute
    const cleaned = value.replace(/[\s\-()]/g, '');
    if (!/^\+?[0-9]{7,15}$/.test(cleaned)) {
      return 'Please enter a valid mobile number (7-15 digits).';
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    // Clear auto-fill banner on any edit
    if (autoFilled) setAutoFilled(false);
    // Live mobile validation
    if (name === 'guestMobile') {
      setMobileError(validateMobile(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent duplicate booking API calls
    if (isBooking) return;

    // Validate mobile before submit
    const mobileErr = validateMobile(formData.guestMobile);
    if (mobileErr) {
      setMobileError(mobileErr);
      return;
    }

    if (!executeRecaptcha) {
      console.error('Recaptcha not yet available');
      return;
    }

    // Save guest details to localStorage for future visits
    try {
      localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify({
        name: formData.guestName,
        email: formData.guestEmail,
        mobile: formData.guestMobile,
      }));
    } catch (err) {
      console.error('Error saving guest details:', err);
    }

    setIsBooking(true);
    try {
      const recaptchaToken = await executeRecaptcha('booking_form');
      await onSubmit({
        ...formData,
        startTime: selectedDateTime,
        eventDetails: eventDetails,
        recaptchaToken,
      });
    } finally {
      setIsBooking(false);
    }
  };

  // Format date and time for display (using current date Nov 02, 2025 as base if needed)
  const formatDateTime = (dateTime) => {
    if (!dateTime) return '';

    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return moment(dateTime)
      .tz(userTimeZone)
      .format('ddd, MMM D, YYYY, h:mm A');
  };

  // Parse duration from eventDetails
  const getDuration = () => {
    if (!eventDetails?.duration) return '30 min';
    try {
      const duration = JSON.parse(eventDetails.duration);
      return duration.normalvalue === 'custom'
        ? duration.customvalue.join(' ')
        : duration.normalvalue;
    } catch {
      return '30 min';
    }
  };

  return (
    <Container fluid className="pd-4 mt-md-4 mx-auto" style={{
      maxWidth: '900px',
      marginLeft: 'auto',
      marginRight: 'auto'
    }}>
      <div className="position-relative">

        {/* Full-screen booking loading overlay */}
        {isBooking && (
          <div className="booking-loading-overlay">
            <div className="booking-loading-content">
              <div className="spinner-border text-primary booking-spinner" role="status">
                <span className="visually-hidden">Booking...</span>
              </div>
              <p className="booking-loading-text">Booking...</p>
            </div>
          </div>
        )}


        <Card className="booking-form-card mx-auto">
          <Card.Body className="p-0">

            {/* Top Section - Event Summary */}
            <div className="event-summary-section">
              <div className="d-flex align-items-center mb-3 header-row">
                <Button
                  variant="outline-secondary"
                  className="back-btn-circle me-3"
                  onClick={onClose}
                >
                  <FaArrowLeft />
                </Button>
                <h3 className="fw-bold text-dark mb-0 event-main-title">
                  {eventDetails?.title || '30 Minute Meeting'}
                </h3>
                {/* Optional pseudo-ribbon area for "Powered by" if needed, left empty to match functionality */}
              </div>

              <div className="event-details-list">
                {/* Duration */}
                <div className="detail-item">
                  <FaClock className="detail-icon" />
                  <span className="fw-semibold text-dark">{getDuration()}</span>
                </div>

                {/* Date & Time */}
                {selectedDateTime && (
                  <div className="detail-item">
                    <FaCalendarAlt className="detail-icon" />
                    <span className="fw-semibold text-dark">
                      {formatDateTime(selectedDateTime)}
                    </span>
                  </div>
                )}

                {/* Timezone */}
                <div className="detail-item">
                  <FaGlobe className="detail-icon" />
                  <span className="fw-semibold text-dark">
                    India Standard Time
                  </span>
                </div>
              </div>
            </div>

            {/* Divider */}
            {/* <hr className="form-divider" /> */}

            {/* Form Section */}
            <div className="form-section">
              <h4 className="fw-bold text-dark mb-4">Enter Details</h4>

              {/* Auto-fill notice */}
              {autoFilled && (
                <div className="autofill-notice mb-4">
                  <FaInfoCircle className="autofill-icon" />
                  <span>Your details have been filled from your previous booking.</span>
                </div>
              )}

              <Form onSubmit={handleSubmit} className="booking-form">
                {/* Name Field */}
                <Form.Group className="mb-4">
                  <Form.Label className="form-label fw-bold">
                    Name *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="guestName"
                    value={formData.guestName}
                    onChange={handleChange}
                    required
                    className="custom-input"
                  />
                </Form.Group>

                {/* Email Field */}
                <Form.Group className="mb-4">
                  <Form.Label className="form-label fw-bold">
                    Email *
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="guestEmail"
                    value={formData.guestEmail}
                    onChange={handleChange}
                    required
                    className="custom-input"
                  />
                </Form.Group>

                {/* Mobile Number Field */}
                <Form.Group className="mb-4">
                  <Form.Label className="form-label fw-bold">
                    Mobile Number *
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    name="guestMobile"
                    value={formData.guestMobile}
                    onChange={handleChange}
                    required
                    isInvalid={!!mobileError}
                    className="custom-input"
                  />
                  <Form.Control.Feedback type="invalid">
                    {mobileError}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Guest Emails */}
                <Form.Group className="mb-4">
                  <Form.Label className="form-label fw-bold">
                    Guest Email(s)
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="guestEmails"
                    value={formData.guestEmails}
                    onChange={handleChange}
                    className="custom-input"
                  />
                  <Form.Text className="text-muted small mt-1 d-block">
                    Notify up to 10 additional guests of the scheduled event.
                  </Form.Text>
                </Form.Group>

                {/* Additional Information */}
                <Form.Group className="mb-4">
                  <Form.Label className="form-label fw-bold">
                    Please share anything that will help prepare for our meeting.
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    className="custom-input"
                  />
                </Form.Group>

                {/* Terms Notice */}
                <div className="terms-notice mb-4">
                  By proceeding, you confirm that you have read and
                  agree to <a href="/TermsAndConditions" target="_blank" rel="noreferrer" className="fw-bold text-decoration-none">Meetify's Terms and Conditions</a> and <a href="/PrivacyPolicy" target="_blank" rel="noreferrer" className="fw-bold text-decoration-none">Privacy Policy</a>.
                </div>

                {/* Schedule Event Button */}
                <div className="mb-4">
                  <Button
                    variant="primary"
                    type="submit"
                    className="schedule-btn fw-bold w-100"
                    disabled={isBooking}
                  >
                    {isBooking ? 'Booking...' : 'Book'}
                  </Button>
                </div>

                {/* Cookie Settings */}
                <div className="cookie-settings-container">
                  <a href="#cookie-settings" className="fw-bold text-decoration-none cookie-link">Cookie settings</a>
                </div>

              </Form>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Custom Styles - Tailwind-like for precision */}
      {/* Custom Styles to match reference image exactly */}
      <style>{`
        :root {
          --calendly-blue: #006bff;
          --calendly-dark: #1a1a1a;
          --calendly-gray: #4d5055;
          --calendly-border: #e2e2e2;
        }

        .booking-form-card {
          max-width: 480px;
          border: 1px solid var(--calendly-border);
          border-radius: 8px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.04);
          background-color: #fff;
          margin-bottom: 2rem;
        }

        .event-summary-section {
          padding: 24px 32px 16px 32px;
        }

        .header-row {
          margin-bottom: 20px !important;
        }

        .back-btn-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 1px solid var(--calendly-border);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--calendly-blue);
          background-color: #fff;
          transition: border-color 0.2s ease;
        }
        .back-btn-circle:hover {
          border-color: #b2b2b2;
          background-color: #fff;
          color: var(--calendly-blue);
        }

        .event-main-title {
          font-size: 1.5rem;
          color: var(--calendly-dark);
          letter-spacing: -0.5px;
        }

        .event-details-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .detail-item {
          display: flex;
          align-items: center;
          color: var(--calendly-gray);
          font-size: 0.95rem;
        }

        .detail-icon {
          width: 20px;
          margin-right: 12px;
          font-size: 1.1rem;
          color: #4d5055;
        }

        .form-divider {
          margin: 0;
          border-color: var(--calendly-border);
          opacity: 1;
        }

        .form-section {
          padding: 24px 32px 32px 32px;
        }

        .form-section h4 {
          font-size: 1.2rem;
          color: var(--calendly-dark);
          letter-spacing: -0.3px;
        }

        .form-label {
          color: var(--calendly-dark);
          font-size: 0.9rem;
          margin-bottom: 6px;
        }

        .custom-input {
          border: 1px solid #cccccc;
          border-radius: 6px;
          padding: 10px 14px;
          font-size: 1rem;
          color: var(--calendly-dark);
          box-shadow: none;
        }
        
        .custom-input:focus {
          border-color: var(--calendly-blue);
          box-shadow: 0 0 0 2px rgba(0, 107, 255, 0.1);
          outline: none;
        }

        .terms-notice {
          font-size: 0.85rem;
          color: var(--calendly-gray);
          line-height: 1.5;
        }

        .terms-notice a {
          color: var(--calendly-blue);
        }

        .schedule-btn {
          background-color: var(--calendly-blue);
          border: none;
          border-radius: 40px; /* Pill shape */
          padding: 14px 24px;
          font-size: 1rem;
          letter-spacing: 0.2px;
          transition: background-color 0.2s ease;
        }
        
        .schedule-btn:hover {
          background-color: #005ce6;
        }

        .cookie-link {
          font-size: 0.85rem;
          color: var(--calendly-blue);
        }

        .autofill-notice {
          display: flex;
          align-items: center;
          gap: 8px;
          background-color: #f0f7ff;
          border: 1px solid #cce0ff;
          padding: 10px 14px;
          border-radius: 6px;
          font-size: 0.85rem;
          color: #004bbf;
        }
        
        .autofill-icon {
          color: var(--calendly-blue);
        }

        /* Mobile specific adjustments to ensure it looks just like the screenshot on small devices */
        @media (max-width: 576px) {
          .event-summary-section, .form-section {
            padding: 20px 24px;
          }
          .booking-form-card {
            border-radius: 0;
            border-left: none;
            border-right: none;
          }
          .event-main-title {
            font-size: 1.35rem;
          }
        }

        /* ── Booking Loading Overlay ── */
        .booking-loading-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.45);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
        }

        .booking-loading-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          padding: 32px 48px;
          background: rgba(255, 255, 255, 0.85);
          border-radius: 16px;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
        }

        .booking-spinner {
          width: 3rem;
          height: 3rem;
          border-width: 0.3em;
        }

        .booking-loading-text {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--calendly-dark, #1a1a1a);
          margin: 0;
          letter-spacing: 0.2px;
        }
      `}</style>
    </Container>
  );
};

const BookingForm = (props) => {
  // IMPORTANT: Replace with your actual reCAPTCHA v3 site key
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeMIhosAAAAAJGApJWwi4TyGluv8IlB02Arwl3Z';

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      <BookingFormComponent {...props} />
    </GoogleReCaptchaProvider>
  );
};


export default BookingForm;